import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, Button, Paper, Avatar, Rating, Chip } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import HubIcon from '@mui/icons-material/Hub';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SpeedIcon from '@mui/icons-material/Speed';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SchoolIcon from '@mui/icons-material/School';
import FactoryIcon from '@mui/icons-material/Factory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SecurityIcon from '@mui/icons-material/Security';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import PublicIcon from '@mui/icons-material/Public';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

// Import new components
import AnimatedCounter from '../components/AnimatedCounter';
import ParticleBackground from '../components/ParticleBackground';

// Import Images (Keep existing ones)
import legacyMigrationImage from '../images/legacy_migration.png';
import southIndianWeddingImg from '../images/south_indian_wedding.jpg';

// 🔹 Data Constants
const heroSlides = [
  {
    tagline: "Navigate Your Next",
    title: "AI-First Enterprise Core",
    desc: "Empowering global organizations to reimagine their business models through Generative AI, predictive analytics, and autonomous decision systems.",
    icon: <AutoGraphIcon sx={{ fontSize: { xs: 150, sm: 200, md: 400 }, color: 'inherit' }} />,
    color: '#00E5FF' // Cyan A400
  },
  {
    tagline: "Digital Acceleration",
    title: "Cloud-Native Agility",
    desc: "Architecting resilient, composable digital ecosystems that allow enterprises to pivot instantly and scale limitless in a volatile market.",
    icon: <HubIcon sx={{ fontSize: { xs: 150, sm: 200, md: 400 }, color: 'inherit' }} />,
    color: '#E040FB' // Purple A200
  },
  {
    tagline: "Global Connectivity",
    title: "Borderlessly Connected",
    desc: "Building low-latency, distributed infrastructures powered by Edge Computing and 5G to unify your global workforce and operations.",
    icon: <CloudIcon sx={{ fontSize: { xs: 150, sm: 200, md: 400 }, color: 'inherit' }} />,
    color: '#2979FF' // Blue A400
  },
  {
    tagline: "Sustainable Future",
    title: "Green Tech Engineering",
    desc: "Pioneering Carbon-Aware Computing and energy-efficient algorithms to drive your ESG goals while maximizing computational performance.",
    icon: <EnergySavingsLeafIcon sx={{ fontSize: { xs: 150, sm: 200, md: 400 }, color: 'inherit' }} />,
    color: '#00C853' // Green A700
  },
  {
    tagline: "Cyber Resilience",
    title: "Zero-Trust Security",
    desc: "Deploying military-grade, AI-driven threat detection and Quantum-Resistant Encryption to safeguard your most critical digital assets.",
    icon: <SecurityIcon sx={{ fontSize: { xs: 150, sm: 200, md: 400 }, color: 'inherit' }} />,
    color: '#FF3D00' // Deep Orange A400
  },
];

const stats = [
  { label: "Years of Experience", value: "4+", color: '#00E5FF' },
  { label: "Projects Delivered", value: "150+", color: '#E040FB' },
  { label: "Awards Won", value: "50+", color: '#2979FF' },
  { label: "Client Retention Rate", value: "98%", color: '#00C853' },
];

const testimonials = [
  { name: "Karthikeyan", role: "CEO, FinTech Solutions", text: "GrowAITech's blockchain architecture was instrumental in securing our Series B funding. A truly strategic partner." },
  { name: "Lakshmi Priya", role: "Director of Digital, OmniBrand", text: "The digital transformation initiative led by GAT resulted in a 300% efficiency gain across our supply chain operations." },
  { name: "Saravanan", role: "CTO, HealthPlus Enterprise", text: "Their AI diagnostic models didn't just improve accuracy; they revolutionized our patient care workflow entirely." },
];

const industries = [
  { name: "FinTech", icon: <AccountBalanceIcon fontSize="large" />, desc: "Secure payment gateways, blockchain ledgers, and algorithmic trading platforms." },
  { name: "Healthcare", icon: <HealthAndSafetyIcon fontSize="large" />, desc: "HIPAA-compliant telemedicine apps, patient portals, and AI diagnostics." },
  { name: "E-Commerce", icon: <ShoppingCartIcon fontSize="large" />, desc: "Scalable marketplaces, personalized recommendation engines, and inventory systems." },
  { name: "EdTech", icon: <SchoolIcon fontSize="large" />, desc: "Interactive learning management systems (LMS) and virtual classrooms." },
  { name: "Manufacturing", icon: <FactoryIcon fontSize="large" />, desc: "IoT integration, supply chain automation, and predictive maintenance." },
  { name: "Logistics", icon: <LocalShippingIcon fontSize="large" />, desc: "Real-time fleet tracking, route optimization, and last-mile delivery tech." }
];

// 🔹 Reusable Components

const SectionHeader = ({ tagline, title, subtitle, center = false, light = false }) => (
  <Box mb={{ xs: 5, md: 8 }} textAlign={center ? "center" : "left"}>
    <Typography variant="overline" sx={{ color: light ? '#69F0AE' : '#1A9E7F', letterSpacing: '0.2em', fontWeight: 700, fontSize: '0.9rem' }}>
      {tagline}
    </Typography>
    <Typography variant="h2" sx={{ fontWeight: 800, mt: 1, color: light ? 'white' : '#002613', fontFamily: '"Lato", sans-serif', fontSize: { xs: '2rem', md: '3.5rem' }, lineHeight: 1.2 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="h6" sx={{ mt: 2, color: light ? 'rgba(255,255,255,0.85)' : '#333', maxWidth: '700px', mx: center ? 'auto' : 0, lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.35rem' }, fontWeight: 400 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

function Home() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = heroSlides[index];

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: '#FAFAFA' }}>

      {/* 🔹 HERO SECTION - PREMIUM & FUTURISTIC */}
      <Box sx={{ position: 'relative', minHeight: { xs: '100vh', md: '110vh' }, display: 'flex', alignItems: 'center', bgcolor: '#002613', overflow: 'hidden', pt: { xs: 20, md: 25 }, pb: { xs: 10, md: 15 } }}>
        {/* Particle Background */}
        <ParticleBackground count={30} color="#1A9E7F" />
        
        {/* Animated Background Elements */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #001e0f 0%, #00331a 100%)', zIndex: 0 }} />
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }} style={{ position: 'absolute', top: '-20%', right: '-10%', width: '1000px', height: '1000px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(29, 233, 182, 0.1) 0%, transparent 60%)', zIndex: 0 }} />
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 100, repeat: Infinity, ease: 'linear' }} style={{ position: 'absolute', bottom: '-40%', left: '-20%', width: '1200px', height: '1200px', border: '1px solid rgba(255,255,255,0.03)', borderRadius: '40%', zIndex: 0 }} />
        
        {/* Floating Orbs */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.2
            }}
            style={{
              position: 'absolute',
              width: `${100 + i * 20}px`,
              height: `${100 + i * 20}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${['#00E5FF', '#E040FB', '#2979FF', '#00C853', '#FF3D00'][i]}20 0%, transparent 70%)`,
              filter: 'blur(40px)',
              top: `${20 + i * 15}%`,
              left: `${10 + i * 18}%`,
              zIndex: 1
            }}
          />
        ))}

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 6 } }}>
          <Grid container spacing={4} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            <Grid item xs={12} md={6}>
              <AnimatePresence mode="wait">
                <Box key={slide.title} color="white" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={{ duration: 0.5 }}>
                    <Chip label="Innovation Redefined" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: slide.color, mb: 3, fontWeight: 700, letterSpacing: 1, border: `1px solid ${slide.color}40` }} />
                    <Typography variant="overline" sx={{ color: slide.color, letterSpacing: 4, fontWeight: 700, display: 'block', mb: 1 }}>{slide.tagline}</Typography>
                    <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' }, fontWeight: 800, lineHeight: 1.1, mb: 3, background: `linear-gradient(to right, #fff, ${slide.color})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {slide.title}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 300, maxWidth: '600px', mb: { xs: 4, md: 6 }, fontSize: { xs: '1rem', md: '1.25rem' }, lineHeight: 1.8, mx: { xs: 'auto', md: 0 } }}>{slide.desc}</Typography>
                    <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      >
                        <Button 
                          component={Link} 
                          to="/contact" 
                          size="large" 
                          className="enhanced-button"
                          sx={{ 
                            borderRadius: 50, 
                            px: 6, 
                            py: 2, 
                            bgcolor: slide.color, 
                            color: '#002613', 
                            '&:hover': { bgcolor: 'white', transform: 'translateY(-2px)' }, 
                            fontWeight: 800, 
                            fontSize: '1rem', 
                            boxShadow: `0 0 20px ${slide.color}60`, 
                            transition: 'all 0.3s',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          Start Your Journey
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      >
                        <Button 
                          component={Link} 
                          to="/services" 
                          size="large" 
                          className="enhanced-button"
                          sx={{ 
                            borderRadius: 50, 
                            px: 6, 
                            py: 2, 
                            border: '1px solid rgba(255,255,255,0.3)', 
                            color: 'white', 
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: slide.color }, 
                            fontWeight: 700, 
                            fontSize: '1rem',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          Our Services
                        </Button>
                      </motion.div>
                    </Box>
                  </motion.div>
                </Box>
              </AnimatePresence>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={slide.tagline}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{ duration: 0.8 }}
                  style={{ position: 'relative' }}
                >
                  {/* Glowing effect behind icon */}
                  <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60%', height: '60%', background: `radial-gradient(circle, ${slide.color}66 0%, transparent 70%)`, filter: 'blur(60px)', zIndex: -1 }} />
                  <Box sx={{ filter: `drop-shadow(0 0 40px ${slide.color}66)`, display: 'flex', justifyContent: 'center', color: slide.color, '& svg': { fontSize: { xs: 180, sm: 250, md: 400 } } }}>
                    {React.cloneElement(slide.icon, { sx: { fontSize: { xs: 180, sm: 250, md: 400 }, color: 'inherit' } })}
                  </Box>
                </motion.div>
              </AnimatePresence>
            </Grid>
          </Grid>
        </Container>

        {/* Scroll Indicator */}
        <Box sx={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', animation: 'bounce 2s infinite', display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ width: 30, height: 50, border: '2px solid rgba(255,255,255,0.3)', borderRadius: 15, position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: 4, height: 8, bgcolor: 'white', borderRadius: 2, mt: 1 }} />
          </Box>
        </Box>
      </Box>

      {/* 🔹 TECH STACK - SCROLLING MARQUEE */}
      <Box sx={{ py: { xs: 4, md: 6 }, borderBottom: '1px solid #eee', bgcolor: 'white', overflow: 'hidden' }}>
        <Typography variant="subtitle2" align="center" color="text.secondary" fontWeight={700} letterSpacing={2} mb={4}>
          POWERED BY NEXT-GEN TECHNOLOGY
        </Typography>

        <Box sx={{ display: 'flex', overflow: 'hidden', width: '100%', position: 'relative', maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)' }}>
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
            style={{ display: "flex", gap: "2rem", whiteSpace: "nowrap", paddingLeft: "1rem", alignItems: 'center' }}
          >
            {[
              { name: "Artificial Intelligence", color: "#F44336" },
              { name: "Python", color: "#FFC107" },
              { name: "React JS", color: "#29B6F6" },
              { name: "Node.js", color: "#66BB6A" },
              { name: "AWS Cloud", color: "#FF9800" },
              { name: "Docker", color: "#0288D1" },
              { name: "Kubernetes", color: "#304FFE" },
              { name: "TensorFlow", color: "#FF6F00" },
              { name: "Flutter", color: "#40C4FF" },
              { name: "Next.js", color: "#000000" },
              { name: "GraphQL", color: "#E040FB" },
              { name: "MongoDB", color: "#4DB6AC" },
              { name: "PostgreSQL", color: "#0277BD" },
              { name: "Cybersecurity", color: "#D32F2F" },
              // Duplicated for seamless loop
              { name: "Artificial Intelligence", color: "#F44336" },
              { name: "Python", color: "#FFC107" },
              { name: "React JS", color: "#29B6F6" },
              { name: "Node.js", color: "#66BB6A" },
              { name: "AWS Cloud", color: "#FF9800" },
              { name: "Docker", color: "#0288D1" },
              { name: "Kubernetes", color: "#304FFE" },
              { name: "TensorFlow", color: "#FF6F00" },
              { name: "Flutter", color: "#40C4FF" },
              { name: "Next.js", color: "#000000" },
              { name: "GraphQL", color: "#E040FB" },
              { name: "MongoDB", color: "#4DB6AC" },
              { name: "PostgreSQL", color: "#0277BD" },
              { name: "Cybersecurity", color: "#D32F2F" }
            ].map((tech, i) => (
              <Typography key={i} variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.25rem', md: '2.125rem' }, color: '#e0e0e0', textTransform: 'uppercase', flexShrink: 0, '&:hover': { color: tech.color, cursor: 'default' }, transition: '0.3s' }}>
                {tech.name}
              </Typography>
            ))}
          </motion.div>
        </Box>
      </Box>

      {/* 🔹 ENGAGEMENT MODEL - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="How We Work" title="Your Journey to Digital Success" subtitle="A transparent, agile, and collaborative process designed to deliver results from Day 1." center />
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {[
              { step: "01", title: "Discovery & Strategy", desc: "We deep dive into your business goals, user needs, and technical constraints to build a solid roadmap.", color: "#00E5FF" },
              { step: "02", title: "Design & Prototype", desc: "Our UX/UI experts craft intuitive interfaces and clickable prototypes to visualize the end product.", color: "#E040FB" },
              { step: "03", title: "Agile Development", desc: "We code in 2-week sprints, ensuring continuous delivery, feedback loops, and rapid iteration.", color: "#2979FF" },
              { step: "04", title: "Launch & Scale", desc: "Rigorous QA testing, seamless deployment, and post-launch support to help you scale globally.", color: "#00C853" }
            ].map((s, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Box sx={{ position: 'relative', p: 4, bgcolor: '#FAFAFA', borderRadius: 4, height: '100%', borderTop: `4px solid ${s.color}`, transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' } }}>
                  <Typography variant="h2" fontWeight={900} sx={{ color: `${s.color}20`, position: 'absolute', top: 10, right: 20 }}>{s.step}</Typography>
                  <Box sx={{ width: 50, height: 50, bgcolor: `${s.color}20`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={800} color={s.color}>{s.step}</Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={800} gutterBottom color="#034522">{s.title}</Typography>
                  <Typography variant="body2" color="#222" lineHeight={1.8} fontSize="1rem">{s.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 SERVICES - GLASSMORPHISM CARDS */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#F0F9F6' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Our Expertise" title="Solutions for the Digital Age" subtitle="We deliver end-to-end technology services designed to scale with your business." center />
          <Grid container spacing={4}>
            {[
              { title: "Custom Web Development", icon: <CodeIcon fontSize="large" />, text: "Scalable, high-performance web applications tailored to your unique business needs using React, Node.js, and modern frameworks.", color: "#00E5FF" },
              { title: "Artificial Intelligence", icon: <AutoGraphIcon fontSize="large" />, text: "Transform data into actionable insights with predictive analytics, NLP, and computer vision models enabling automation.", color: "#E040FB" },
              { title: "Cloud & Infrastructure", icon: <CloudIcon fontSize="large" />, text: "Secure, scalable cloud architecture on AWS and Azure. We handle migration, optimization, and DevOps automation.", color: "#2979FF" },
              { title: "Cybersecurity", icon: <SecurityIcon fontSize="large" />, text: "Comprehensive threat protection, penetration testing, and zero-trust security implementation for enterprise assets.", color: "#FF3D00" },
              { title: "Mobile App Development", icon: <SpeedIcon fontSize="large" />, text: "Native and cross-platform mobile apps that deliver seamless user experiences on iOS and Android devices.", color: "#FFD740" },
              { title: "Digital Consulting", icon: <LightbulbIcon fontSize="large" />, text: "Strategic technology roadmapping to help your business navigate digital transformation and market shifts.", color: "#00C853" },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <FadeIn delay={i * 0.1}>
                  <motion.div
                    whileHover={{ y: -10 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <MotionCard item={item} />
                  </motion.div>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
          <Box textAlign="center" mt={8}>
            <Button component={Link} to="/services" variant="outlined" size="large" sx={{ borderRadius: 50, px: 5, py: 1.5, borderColor: '#034522', color: '#034522', fontWeight: 700, '&:hover': { bgcolor: '#034522', color: 'white' } }}>
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* 🔹 CORE CAPABILITIES - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Why We Lead" title="Our Core Capabilities" subtitle="Beyond coding, we bring a holistic ecosystem of expertise to drive your business forward." center />

          <Grid container spacing={4}>
            {[
              { title: "Cloud-Native Architecture", desc: "Building resilient , scalable systems that thrive in the cloud era.", icon: <CloudIcon /> },
              { title: "DevSecOps Integration", desc: "Embedding security and automation into the heart of the development lifecycle.", icon: <SecurityIcon /> },
              { title: "Data Engineering", desc: "Transforming raw data into high-value strategic assets for decision making.", icon: <HubIcon /> },
              { title: "Experience Design (XD)", desc: "Crafting intuitive, human-centric interfaces that delight users.", icon: <LightbulbIcon /> },
              { title: "Legacy Modernization", desc: "Breathing new life into aging systems with microservices strategies.", icon: <AutoGraphIcon /> },
              { title: "IoT Ecosystems", desc: "Connecting the physical and digital worlds for smarter operations.", icon: <HubIcon /> }
            ].map((cap, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{ p: 4, height: '100%', border: '1px solid #eee', borderRadius: 4, transition: '0.3s', '&:hover': { bgcolor: '#F0F9F6', borderColor: '#1A9E7F' } }}>
                  <Box sx={{ color: '#1A9E7F', mb: 2 }}>{cap.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom color="#034522">{cap.title}</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{cap.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 DIGITAL TRANSFORMATION FRAMEWORK - NEW VISUAL SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white', position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.1, background: 'repeating-linear-gradient(45deg, #000, #000 10px, #034522 10px, #034522 20px)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <SectionHeader tagline="The Blueprint" title="Our Transformation Framework" subtitle="A structured approach to digital maturity." light center />

          <Grid container spacing={4} justifyContent="center" alignItems="center">
            {[
              { step: "01", title: "Assess", desc: "Tech Audit & Gap Analysis" },
              { step: "02", title: "Strategize", desc: "Roadmap & Architecture" },
              { step: "03", title: "Execute", desc: "Agile Development & QA" },
              { step: "04", title: "Optimize", desc: "Performance Tuning & Scale" }
            ].map((phase, i) => (
              <Grid item xs={12} md={3} key={i} sx={{ position: 'relative' }}>
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 50, width: '100%', aspectRatio: '1/1', display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)', transition: '0.3s', '&:hover': { bgcolor: '#1A9E7F', borderColor: '#1A9E7F', transform: 'scale(1.05)' } }}>
                  <Typography variant="h2" fontWeight={900} sx={{ opacity: 0.2, mb: -2 }}>{phase.step}</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ my: 1 }}>{phase.title}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>{phase.desc}</Typography>
                </Box>
                {i < 3 && <ArrowForwardIcon sx={{ position: 'absolute', top: '50%', right: -20, transform: 'translateY(-50%)', display: { xs: 'none', md: 'block' }, color: '#69F0AE', fontSize: 30 }} />}
                {i < 3 && <Box sx={{ position: 'absolute', bottom: -30, left: '50%', transform: 'translateX(-50%) rotate(90deg)', display: { xs: 'block', md: 'none' }, color: '#69F0AE' }}><ArrowForwardIcon /></Box>}
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 THE GAT ADVANTAGE - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <SectionHeader tagline="Why Choose Us" title="The GAT Advantage" />
              <Typography paragraph sx={{ color: '#222', fontSize: '1.15rem', lineHeight: 1.8, mb: 4, fontWeight: 500 }}>
                We are not just a technology vendor; we are your strategic innovation partner. Our unique synthesis of engineering excellence, vertical industry depth, and global delivery capability sets us apart in the digital economy.
              </Typography>
              {[
                "Top 1% Global Engineering Talent",
                "Time Zone Aligned Development",
                "Enterprise-Grade Security (ISO 27001)",
                "Transparent Pricing & No Hidden Costs"
              ].map((item, i) => (
                <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                  <Box sx={{ width: 24, height: 24, bgcolor: '#1A9E7F', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <Typography variant="caption" fontWeight={900}>✓</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight={600} color="#333">{item}</Typography>
                </Box>
              ))}
              <Button component={Link} to="/about" variant="contained" size="large" sx={{ mt: 4, borderRadius: 50, px: 5, bgcolor: '#034522', '&:hover': { bgcolor: '#1A9E7F' } }}>
                About Our Culture
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box component="img" src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=600&q=80" sx={{ width: '100%', borderRadius: 4, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }} />
                  <Paper sx={{ p: 3, mt: -4, mx: 2, position: 'relative', zIndex: 1, bgcolor: '#002613', color: 'white', borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                    <Typography variant="h4" fontWeight={800} color="#69F0AE">98%</Typography>
                    <Typography variant="body2">Client Retention Rate</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sx={{ pt: 8 }}>
                  <Paper sx={{ p: 3, mb: -4, mx: 2, position: 'relative', zIndex: 1, bgcolor: '#F0F9F6', borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                    <Typography variant="h4" fontWeight={800} color="#034522">50+</Typography>
                    <Typography variant="body2" fontWeight={600}>Awards Won</Typography>
                  </Paper>
                  <Box component="img" src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?auto=format&fit=crop&w=600&q=80" sx={{ width: '100%', borderRadius: 4, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 FLAGSHIP PRODUCT - SMARTSYNC ERP */}
      <Box sx={{ py: { xs: 8, md: 15 }, background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', position: 'relative', overflow: 'hidden', color: 'white' }}>
        {/* Decorative elements */}
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: -150, left: -150, width: 500, height: 500, background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)', borderRadius: '50%' }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" sx={{ color: '#fbcfe8', fontWeight: 800, letterSpacing: 4 }}>FLAGSHIP PRODUCT</Typography>
            <Typography variant="h2" fontWeight={900} sx={{ mt: 1, mb: 2, fontSize: { xs: '2.5rem', md: '4rem' } }}>SmartSync ERP</Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', maxWidth: '800px', mx: 'auto', fontWeight: 400 }}>
              The high-performance, premium ecosystem designed to unify your organizational workflows and provide real-time operational insights.
            </Typography>
          </Box>

          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Box 
                  component="img" 
                  src={require('../images/smartsync_logo.png')} 
                  sx={{ width: '200px', mb: 4, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))' }} 
                />
                <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#fff' }}>Unify. Optimize. Excel.</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
                  SmartSync ERP replaces fragmented systems with a centralized, data-driven core. Designed with a state-of-the-art UI and glassmorphism philosophy, it delivers a tactile, high-end experience for both administration and employees.
                </Typography>

                <Grid container spacing={3} mb={5}>
                  {[
                    { 
                      title: "Admin Control Center", 
                      desc: "Total oversight with real-time analytics and lifecycle management.", 
                      icon: "🎯",
                      subFeatures: ["Real-time Analytics", "Employee Lifecycle", "Task Orchestration", "Attendance Oversight"]
                    },
                    { 
                      title: "Employee Portal", 
                      desc: "Self-service dashboard with smart attendance and task orchestration.", 
                      icon: "👥",
                      subFeatures: ["Smart Attendance", "Self-Service Dashboard", "Team Collaboration", "Task Management"]
                    }
                  ].map((feat, i) => (
                    <Grid item xs={12} key={i}>
                      <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.2)' }}>
                        <Box display="flex" gap={2} mb={1}>
                          <Typography variant="h4">{feat.icon}</Typography>
                          <Typography variant="h6" fontWeight={800}>{feat.title}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>{feat.desc}</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {feat.subFeatures.map((sub, j) => (
                            <Chip 
                              key={j} 
                              label={sub} 
                              size="small" 
                              sx={{ 
                                bgcolor: 'rgba(255,255,255,0.05)', 
                                color: 'white', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                fontSize: '0.7rem'
                              }} 
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    component="a"
                    href="https://smart-sync-jp6b4noav-growaitechs-projects.vercel.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: 'white',
                      color: '#4f46e5',
                      fontWeight: 800,
                      borderRadius: 50,
                      px: 5, py: 1.5,
                      '&:hover': { bgcolor: '#f3f4f6', transform: 'translateY(-2px)' },
                      transition: 'all 0.3s'
                    }}
                  >
                    View Live Demo
                  </Button>
                  <Box display="flex" gap={1} alignItems="center">
                    {['React', 'MUI', 'Recharts'].map((tech, i) => (
                      <Chip key={i} label={tech} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', p: { xs: 2, md: 4 } }}>
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', zIndex: 0 }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box 
                    component="img" 
                    src={require('../images/smartsync_dashboard.png')} 
                    sx={{ 
                      width: '100%', 
                      borderRadius: 4, 
                      boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                      transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                      transition: '0.5s',
                      '&:hover': { transform: 'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.02)' }
                    }} 
                  />
                  
                  <Paper sx={{ 
                    position: 'absolute', 
                    bottom: -20, 
                    right: -20, 
                    p: 3, 
                    borderRadius: 4, 
                    bgcolor: 'white', 
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    display: { xs: 'none', md: 'block' }
                  }}>
                    <Typography variant="h6" fontWeight={900} color="#4f46e5">+150%</Typography>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">EFFICIENCY GAIN</Typography>
                  </Paper>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 FEATURED PRODUCT - KONGU KALYANAM */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FFF8E1', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Built by GrowAITech" title="Digital innovation in Action" subtitle="We turn ideas into scalable, high-performance digital products." center />

          <Grid container spacing={8} alignItems="center" sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: -30, left: -30, width: 100, height: 100, bgcolor: '#FFD740', borderRadius: '50%', opacity: 0.2 }} />
                <Typography variant="overline" sx={{ color: '#FF6F00', fontWeight: 800, letterSpacing: 2 }}>COMMUNITY MATRIMONY PLATFORM</Typography>
                <Typography variant="h2" fontWeight={900} sx={{ color: '#BF360C', mb: 2, lineHeight: 1.1 }}>
                  Kongu Kalyanam
                </Typography>
                <Typography variant="h6" sx={{ color: '#5D4037', mb: 4, lineHeight: 1.6, fontWeight: 400 }}>
                  A transformative digital platform designed to connect hearts with privacy, simplicity, and cultural integrity. Experience the future of community matrimony.
                </Typography>

                <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                  {['React Web', 'Flutter App', 'Firebase Backend'].map((tech, i) => (
                    <Chip key={i} label={tech} sx={{ bgcolor: 'white', color: '#BF360C', fontWeight: 700, border: '1px solid #FFCCBC' }} />
                  ))}
                </Box>

                <Box sx={{ mb: 5 }}>
                  {[
                    { title: "Smart Match", desc: "AI-driven algorithms to find the perfect life partner.", icon: "❤️" },
                    { title: "Privacy & Security", desc: "End-to-end encryption ensuring data safety.", icon: "🔒" },
                    { title: "Scalable & Fast", desc: "High-performance architecture for seamless usage.", icon: "⚡" }
                  ].map((feat, i) => (
                    <Box key={i} display="flex" gap={2} mb={2} alignItems="flex-start">
                      <Box sx={{ minWidth: 40, height: 40, borderRadius: '50%', bgcolor: '#FFCCBC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        {feat.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} color="#BF360C">{feat.title}</Typography>
                        <Typography variant="body2" color="#5D4037">{feat.desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box display="flex" gap={2}>
                  <Button
                    href="https://play.google.com/store/apps/details?id=com.kongukalyanam.app"
                    target="_blank"
                    size="large"
                    variant="contained"
                    sx={{
                      bgcolor: '#BF360C',
                      color: 'white',
                      px: 4, py: 1.5,
                      borderRadius: 50,
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#D84315' }
                    }}
                  >
                    View Live Project
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', p: 2 }}>
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#FFECB3', transform: 'rotate(3deg)', borderRadius: 8, zIndex: 0 }} />
                {/* Placeholder for the product image - User should replace the src with their actual image path */}
                <Box
                  component="img"
                  src={southIndianWeddingImg}
                  sx={{
                    width: '100%',
                    borderRadius: 6,
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 25px 50px rgba(191, 54, 12, 0.2)',
                    transform: 'rotate(-2deg)',
                    transition: '0.3s',
                    '&:hover': { transform: 'rotate(0deg) scale(1.02)' }
                  }}
                />

                <Paper sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -20,
                  p: 3,
                  borderRadius: 4,
                  bgcolor: 'white',
                  zIndex: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  maxWidth: 220
                }}>
                  <Typography variant="h6" fontWeight={800} color="#BF360C" lineHeight={1.2}>
                    4.9/5 Rating
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trusted by the Community
                  </Typography>
                  <Rating value={5} readOnly size="small" sx={{ mt: 1, color: '#FFD740' }} />
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 500, height: 500, background: 'radial-gradient(circle, rgba(105, 240, 174, 0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionHeader tagline="Innovation Redefined" title="Sustainable Future" subtitle="We believe that technology must be a force for good. Our Green Tech Engineering initiatives are designed to reduce digital carbon footprints while driving high-performance computing." light />

              <Box sx={{ display: 'flex', gap: 3, mb: 5, mt: 4, alignItems: 'flex-start' }}>
                <EnergySavingsLeafIcon sx={{ fontSize: 48, color: '#69F0AE', mt: 1, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: '#fff' }}>Green Tech Engineering</Typography>
                  <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.8, fontSize: '1.05rem', color: '#E0E0E0' }}>
                    Pioneering Carbon-Aware Computing and energy-efficient algorithms to drive your ESG goals while maximizing computational performance. We optimize code to minimize energy consumption without compromising on speed.
                  </Typography>
                </Box>
              </Box>

              <Button component={Link} to="/contact" size="large" sx={{ borderRadius: 50, px: 5, py: 1.5, bgcolor: '#69F0AE', color: '#002613', fontWeight: 800, textTransform: 'none', fontSize: '1.1rem', '&:hover': { bgcolor: 'white' } }}>
                Partner for Impact
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {[
                  { title: "Carbon-Aware Computing", desc: "Workloads scheduled during low-carbon intensity windows.", icon: <PublicIcon /> },
                  { title: "Energy-Efficient AI", desc: "Optimizing model training to reduce GPU power consumption by up to 40%.", icon: <LightbulbIcon /> },
                  { title: "Sustainable Cloud", desc: "Serverless architectures that scale to zero, eliminating idle resource waste.", icon: <CloudIcon /> },
                  { title: "ESG Analytics", desc: "Real-time dashboards to monitor and report your digital carbon footprint.", icon: <WorkspacePremiumIcon /> }
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, height: '100%', border: '1px solid rgba(255,255,255,0.1)', transition: '0.3s', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                      <Box sx={{ color: '#69F0AE', mb: 2 }}>{item.icon}</Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontSize: '1.1rem', color: 'white' }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7, lineHeight: 1.6, color: 'white' }}>{item.desc}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 GLOBAL DELIVERY MODEL - CONTENT EXPANSION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Global Scale" title="Flexible Delivery Models" subtitle="We offer a hybrid engagement framework designed to optimize cost, speed, and collaboration." center />
          <Grid container spacing={4} mt={4}>
            {[
              { title: "Onsite Model", icon: <HandshakeIcon fontSize="large" />, desc: "Our experts work directly from your office, ensuring maximum collaboration, security, and cultural alignment for critical initiatives.", color: "#2979FF" },
              { title: "Offshore Model", icon: <PublicIcon fontSize="large" />, desc: "Leverage our state-of-the-art development centers in India to reduce costs by up to 60% while maintaining 24/7 productivity.", color: "#00C853" },
              { title: "Hybrid Model", icon: <CloudIcon fontSize="large" />, desc: "The best of both worlds. Local project management combined with global engineering muscle for speed and scalability.", color: "#FF9800" }
            ].map((model, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper elevation={0} sx={{ p: 5, height: '100%', borderRadius: 6, bgcolor: '#FAFAFA', borderTop: `6px solid ${model.color}`, transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
                  <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: model.color, mb: 3, boxShadow: '0 10px 20px rgba(0,0,0,0.05)' }}>
                    {model.icon}
                  </Box>
                  <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#034522', fontSize: '1.5rem' }}>{model.title}</Typography>
                  <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8, mb: 3 }}>{model.desc}</Typography>
                  <Box sx={{ pt: 3, borderTop: '1px solid #eee' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Best For:</Typography>
                    <Typography variant="body2" fontWeight={600} color={model.color} mt={1}>{i === 0 ? "Strategic Transformation" : i === 1 ? "Product Engineering" : "Agile Scaling"}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 STATS - IMPACT SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionHeader tagline="Our Impact" title="Numbers That Speak" light />
              <Typography fontSize="1.1rem" sx={{ opacity: 0.8, lineHeight: 1.8, mb: 4 }}>
                We measure our success by the success of our clients. Our data-driven approach ensures that every line of code we write translates into measurable business value.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                {stats.map((stat, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Paper 
                        elevation={0} 
                        className="enhanced-card"
                        sx={{ 
                          p: { xs: 3, md: 4 }, 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column', 
                          justifyContent: 'center', 
                          bgcolor: 'rgba(255,255,255,0.05)', 
                          borderRadius: 4, 
                          textAlign: 'center', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': { 
                            transform: 'translateY(-10px) scale(1.02)', 
                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                            bgcolor: 'rgba(255,255,255,0.1)'
                          }
                        }}
                      >
                        <AnimatedCounter
                          end={stat.value.includes('+') ? parseInt(stat.value) : parseFloat(stat.value)}
                          suffix={stat.value.includes('+') ? '+' : stat.value.includes('%') ? '%' : ''}
                          duration={2}
                          delay={i * 0.2}
                          color={stat.color}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            textTransform: 'uppercase', 
                            letterSpacing: 1, 
                            fontWeight: 700, 
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            mt: 1
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 🔹 INDUSTRIES - TABS STYLE */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Verticals" title="Industries We Transform" center />
          <Grid container spacing={4}>
            {industries.map((ind, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <FadeIn delay={i * 0.1}>
                  <Paper elevation={0} sx={{ p: 4, height: '100%', borderRadius: 4, bgcolor: '#fff', border: '1px solid #f0f0f0', transition: 'all 0.3s', '&:hover': { borderColor: '#1A9E7F', transform: 'translateY(-5px)', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' } }}>
                    <Box sx={{ color: '#1A9E7F', mb: 3 }}>{ind.icon}</Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#034522' }}>{ind.name}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>{ind.desc}</Typography>
                  </Paper>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 TESTIMONIALS - ENHANCED */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#F9FAFB' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Client Stories" title="Trusted by Leaders" subtitle="Don't just take our word for it. Here is what our partners have to say about working with GrowAITech." center />
          <Grid container spacing={4}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} md={4} key={i}>
                <FadeIn delay={i * 0.1}>
                  <Paper elevation={0} sx={{ p: 5, height: '100%', borderRadius: 6, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', position: 'relative', overflow: 'hidden' }}>
                    <FormatQuoteIcon sx={{ fontSize: 60, color: 'rgba(26,158,127,0.1)', position: 'absolute', top: 20, right: 20 }} />
                    <Rating value={5} readOnly sx={{ color: '#1A9E7F', mb: 3 }} />
                    <Typography fontSize="1.1rem" color="#444" mb={4} sx={{ lineHeight: 1.8, fontStyle: 'italic' }}>"{t.text}"</Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: '#034522', fontWeight: 800 }}>{t.name[0]}</Avatar>
                      <Box>
                        <Typography fontWeight={700} sx={{ color: '#034522' }}>{t.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{t.role}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 LATEST INSIGHTS - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={8}>
            <Box>
              <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Thought Leadership</Typography>
              <Typography variant="h2" fontWeight={800} sx={{ color: '#034522' }}>Latest Insights</Typography>
            </Box>
            <Button component={Link} to="/news" variant="text" sx={{ color: '#1A9E7F', fontWeight: 700 }}>
              View All Articles <ArrowForwardIcon sx={{ ml: 1, fontSize: 18 }} />
            </Button>
          </Box>
          <Grid container spacing={4}>
            {[
              { title: "The Future of GenAI in Enterprise", category: "Innovation", image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=600&q=80" },
              { title: "Migrating Legacy Monoliths to Microservices", category: "Engineering", image: legacyMigrationImage },
              { title: "Why Cybersecurity is a Boardroom Conversation", category: "Security", image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=600&q=80" },
              { title: "Optimizing Cloud Costs with Serverless", category: "Cloud", image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80" },
              { title: "UX Trends Defining 2025 Mobile Apps", category: "Design", image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=600&q=80" },
              { title: "Building Resilient Supply Chains with IoT", category: "IoT", image: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&w=600&q=80" }
            ].map((post, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper elevation={0} sx={{ height: '100%', borderRadius: 4, overflow: 'hidden', border: '1px solid #eee', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' } }}>
                  <Box component="img" src={post.image} sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                  <Box p={3}>
                    <Chip label={post.category} size="small" sx={{ bgcolor: '#E8F5E9', color: '#1A9E7F', fontWeight: 700, mb: 2 }} />
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#034522', fontSize: '1.25rem', lineHeight: 1.4 }}>{post.title}</Typography>
                    <Button component={Link} to="/news" sx={{ p: 0, color: '#034522', fontWeight: 700, mt: 1 }}>Read Article</Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 STRATEGIC PARTNERS - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#F9FAFB' }}>
        <Container maxWidth="lg">
          <SectionHeader tagline="Our Ecosystem" title="Strategic Partners" subtitle="We collaborate with the world's leading technology providers to deliver enterprise-grade solutions." center />
          <Grid container spacing={4} justifyContent="center" alignItems="center" sx={{ opacity: 0.7 }}>
            {[
              "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
              "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg",
              "https://upload.wikimedia.org/wikipedia/commons/5/51/Google_Cloud_logo.svg",
              "https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg",
              "https://upload.wikimedia.org/wikipedia/commons/5/59/SAP_2011_logo.svg",
              "https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg"
            ].map((logo, i) => (
              <Grid item xs={6} md={2} key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'center', filter: 'grayscale(100%)', transition: '0.3s', '&:hover': { filter: 'grayscale(0%)', transform: 'scale(1.1)' } }}>
                  <Box component="img" src={logo} sx={{ height: 50, maxWidth: '100%', objectFit: 'contain' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 FAQ SECTION - NEW CONTENT */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="md">
          <SectionHeader tagline="Common Queries" title="Frequently Asked Questions" subtitle="Everything you need to know about partnering with GrowAITech." center />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { q: "How do you ensure data security and IP protection?", a: "We take security seriously. GrowAITech is ISO 27001 certified. We sign strict NDAs (Non-Disclosure Agreements) before any discussion. Your Intellectual Property (IP) is fully yours, and we adhere to GDPR, HIPAA, and industry-standard encryption protocols for all data handling." },
              { q: "What is your typical engagement model?", a: "We offer flexible models: Fixed Cost (for well-defined scope), Time & Material (for evolving requirements), and Dedicated Teams (for long-term extensions of your workforce). We recommend the best fit based on your project's maturity and goals." },
              { q: "Can you work with our existing development team?", a: "Absolutely. Our 'Hybrid Model' is designed for this. We integrate seamlessly into your existing workflows (Jira, Slack, GitHub) and act as a force multiplier, filling skill gaps or taking ownership of specific modules." },
              { q: "What technologies do you specialize in?", a: "Our core strengths lie in the MERN Stack (MongoDB, Express, React, Node.js), Python/Django for AI & Backend, Flutter for Mobile, and Cloud DevOps (AWS/Azure). We also have niche expertise in Blockchain (Hyperledger/Solidity) and IoT embedded systems." },
              { q: "How do you handle post-launch support?", a: "Deployment is just the beginning. We offer tiered support packages including 24/7 monitoring, SLA-backed bug fixes, and continuous feature enhancements. We ensure your application stays secure, updated, and performant." },
              { q: "What is the estimated timeline for an MVP?", a: "While every project is unique, a typical MVP (Minimum Viable Product) takes 8-12 weeks from Discovery to Launch. We prioritize core features to get you to market quickly, then iterate based on user feedback." }
            ].map((faq, i) => (
              <Paper key={i} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 3, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#FAFAFA' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#034522', fontSize: '1.1rem' }}>{faq.q}</Typography>
                </Box>
                <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #eee' }}>
                  <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.7 }}>{faq.a}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 🔹 CTA SECTION - BOLD */}
      <Box sx={{ py: { xs: 8, md: 15 }, background: 'linear-gradient(135deg, #034522 0%, #002613 100%)', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'radial-gradient(circle at center, rgba(29, 233, 182, 0.1) 0%, transparent 70%)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" fontWeight={900} mb={3}>Ready to Transform Your Business?</Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 6, fontWeight: 300, fontSize: '1.25rem' }}>Let's build the future together. Schedule a free consultation with our experts.</Typography>
          <Box display="flex" gap={3} justifyContent="center" flexDirection={{ xs: 'column', sm: 'row' }}>
            <Button component={Link} to="/contact" size="large" sx={{ bgcolor: '#1DE9B6', color: '#002613', px: 6, py: 2, borderRadius: 50, fontWeight: 800, fontSize: '1.1rem', '&:hover': { bgcolor: '#00BFA5', transform: 'scale(1.05)', boxShadow: '0 0 20px rgba(29, 233, 182, 0.5)' }, transition: 'all 0.3s' }}>
              Get a Quote
            </Button>
            <Button component={Link} to="/careers" size="large" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', px: 6, py: 2, borderRadius: 50, fontWeight: 700, fontSize: '1.1rem', '&:hover': { bgcolor: 'white', color: '#034522' } }}>
              Join Our Team
            </Button>
          </Box>
        </Container>
      </Box>

    </Box>
  );
}

// Sub-component for Service Cards to keep main code clean
const MotionCard = ({ item }) => (
  <Paper 
    className="enhanced-card"
    component={motion.div}
    whileHover={{ y: -10, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    onClick={() => window.location.href = '/services'}
    sx={{ 
      p: 5, 
      height: '100%', 
      borderRadius: 4, 
      bgcolor: 'white', 
      border: '1px solid #f0f0f0', 
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
      '&:hover': { 
        transform: 'translateY(-10px) scale(1.02)', 
        boxShadow: `0 20px 50px ${item.color}30`, 
        borderColor: item.color 
      }, 
      position: 'relative', 
      overflow: 'hidden',
      cursor: 'pointer'
    }}
  >
    {/* Animated gradient overlay */}
    <Box 
      sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        background: `linear-gradient(135deg, ${item.color}10 0%, transparent 50%)`,
        opacity: 0,
        transition: 'opacity 0.3s',
        '.MuiPaper-root:hover &': { opacity: 1 }
      }} 
    />
    
    {/* Top accent bar */}
    <Box 
      sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '4px', 
        bgcolor: 'transparent', 
        transition: 'all 0.3s', 
        '.MuiPaper-root:hover &': { 
          bgcolor: item.color,
          boxShadow: `0 0 10px ${item.color}60`
        } 
      }} 
    />
    
    {/* Icon with hover effect */}
    <motion.div
      whileHover={{ scale: 1.1, rotate: 5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Box 
        sx={{ 
          width: 70, 
          height: 70, 
          borderRadius: 3, 
          bgcolor: `${item.color}15`, 
          color: item.color, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mb: 3,
          transition: 'all 0.3s',
          '.MuiPaper-root:hover &': {
            bgcolor: `${item.color}25`,
            transform: 'scale(1.05)'
          }
        }}
      >
        {item.icon}
      </Box>
    </motion.div>
    
    <Typography 
      variant="h5" 
      fontWeight={700} 
      gutterBottom 
      sx={{ 
        color: '#034522',
        transition: 'color 0.3s',
        '.MuiPaper-root:hover &': { color: item.color }
      }}
    >
      {item.title}
    </Typography>
    
    <Typography 
      variant="body1" 
      sx={{ 
        color: '#333', 
        mb: 3, 
        fontWeight: 500, 
        lineHeight: 1.8,
        transition: 'color 0.3s',
        '.MuiPaper-root:hover &': { color: '#555' }
      }}
    >
      {item.text}
    </Typography>
    
    <motion.div
      whileHover={{ x: 5 }}
      transition={{ type: "spring", stiffness: 400 }}
    >
      <Link 
        to="/services" 
        style={{ 
          textDecoration: 'none', 
          color: item.color, 
          fontWeight: 800, 
          display: 'flex', 
          alignItems: 'center', 
          marginTop: 'auto',
          transition: 'all 0.3s'
        }}
      >
        Learn More <ArrowForwardIcon sx={{ fontSize: 18, ml: 1, transition: 'transform 0.3s' }} />
      </Link>
    </motion.div>
  </Paper>
);

export default Home;
